/**
=========================================================
* Material Dashboard 3 PRO React - v2.3.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Drawer from "@mui/material/Drawer";
import { styled } from "@mui/material/styles";

export default styled(Drawer)(({ theme, ownerState }) => {
  const { palette, boxShadows, transitions, breakpoints, functions } = theme;
  const { transparentSidenav, whiteSidenav, miniSidenav, darkMode } =
    ownerState;

  const sidebarWidth = 222;
  const { transparent, gradients, white, background, grey } = palette;
  const { xs } = boxShadows;
  const { pxToRem, linearGradient } = functions;

  // Prefer SBI lighter teal for sidebar when available; never use white when teal is set
  const tealSidenav = palette.backgroundSbi?.secondary ?? background.sidenav;
  let backgroundValue =
    tealSidenav ||
    (darkMode
      ? background.sidenav
      : linearGradient(gradients.dark.main, gradients.dark.state));

  if (transparentSidenav) {
    backgroundValue = transparent.main;
  } else if (whiteSidenav && !tealSidenav) {
    backgroundValue = white.main;
  }

  // styles for the sidenav when miniSidenav={false}
  const drawerOpenStyles = () => ({
    margin: "8px",
    borderRadius: "8px",
    background: backgroundValue,
    transform: "translateX(0)",
    transition: transitions.create("transform", {
      easing: transitions.easing.sharp,
      duration: transitions.duration.shorter,
    }),

    [breakpoints.up("xl")]: {
      boxShadow: transparentSidenav ? "none" : xs,
      marginBottom: transparentSidenav ? 0 : "inherit",
      left: "0",
      width: sidebarWidth,
      transform: "translateX(0)",
      transition: transitions.create(["width", "background-color"], {
        easing: transitions.easing.sharp,
        duration: transitions.duration.enteringScreen,
      }),
    },
  });

  // styles for the sidenav when miniSidenav={true}
  const drawerCloseStyles = () => ({
    background: backgroundValue,
    transform: `translateX(${pxToRem(-320)})`,
    transition: transitions.create("transform", {
      easing: transitions.easing.sharp,
      duration: transitions.duration.shorter,
    }),

    [breakpoints.up("xl")]: {
      boxShadow: transparentSidenav ? "none" : xs,
      marginBottom: transparentSidenav ? 0 : "inherit",
      left: "0",
      width: pxToRem(96),
      overflowX: "hidden",
      transform: "translateX(0)",
      transition: transitions.create(["width", "background-color"], {
        easing: transitions.easing.sharp,
        duration: transitions.duration.shorter,
      }),
    },
  });

  const borderColor = tealSidenav
    ? palette.borderSbi?.subtle ?? palette.grey?.[600] ?? grey[700]
    : darkMode
    ? grey[800]
    : grey[200];

  return {
    "& .MuiDrawer-paper": {
      boxShadow: xs,
      border: transparentSidenav ? "none" : `1px solid ${borderColor}`,
      backgroundColor: `${backgroundValue} !important`,
      background: `${backgroundValue} !important`,
      ...(miniSidenav ? drawerCloseStyles() : drawerOpenStyles()),
    },
  };
});
